<template>
  <div class="relative w-full">
    <!-- Search Bar that expands on focus -->
    <div class="bg-white rounded-lg shadow-sm p-2 flex items-center">
      <div class="bg-indigo-100 p-2 rounded-full mr-3">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>
      <input
        type="text"
        placeholder="Ask about your dashboard data..."
        class="transition-all duration-300 focus:outline-none"
        :class="{ 'w-full': isSearchFocused, 'w-64': !isSearchFocused }"
        v-model="searchQuery"
        @focus="isSearchFocused = true"
        @blur="handleBlur"
        @keyup.enter="handleSearchSubmit"
        :disabled="isLoading"
      />
      <button
        @click="handleSearchSubmit"
        :disabled="isLoading || !searchQuery.trim()"
        class="ml-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="hidden sm:inline">{{ isLoading ? 'Asking...' : 'Ask AI' }}</span>
        <svg v-if="!isLoading" xmlns="http://www.w3.org/2000/svg" class="ml-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M5 12h14M12 5l7 7-7 7"></path>
        </svg>
        <div v-else class="ml-2 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      </button>
    </div>

    <!-- Chat Dropdown with Fixed Height -->
    <div v-if="showAIResponse" class="absolute inset-x-0 top-full mt-2 z-50">
      <div class="bg-white rounded-lg shadow-lg border border-gray-200">
        <!-- Chat Header -->
        <div class="flex justify-between items-center p-4 border-b border-gray-200">
          <div class="flex items-center">
            <div class="bg-indigo-100 p-2 rounded-full mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 3l1.88 5.76h6.12l-4.94 3.59 1.88 5.76-4.94-3.59-4.94 3.59 1.88-5.76-4.94-3.59h6.12z"></path>
              </svg>
            </div>
            <h3 class="font-medium text-gray-800">AI Assistant</h3>
          </div>
          <div class="flex items-center">
            <button @click="startNewChat" class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-md text-sm mr-2">
              New Chat
            </button>
            <button @click="showAIResponse = false" class="text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Chat Messages with Fixed Height -->
        <div class="h-64 overflow-y-auto p-4" ref="messageContainer">
          <!-- User Messages -->
          <div v-for="(message, index) in chatMessages" :key="index" class="mb-4">
            <!-- AI Message -->
            <div v-if="message.sender === 'ai'" class="flex items-start mb-4">
              <div class="bg-indigo-100 p-2 rounded-full mr-3 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 3l1.88 5.76h6.12l-4.94 3.59 1.88 5.76-4.94-3.59-4.94 3.59 1.88-5.76-4.94-3.59h6.12z"></path>
                </svg>
              </div>
              <div class="bg-white rounded-lg p-4 shadow-sm max-w-3xl">
                <p class="text-gray-700 whitespace-pre-wrap">{{ message.text }}</p>
              </div>
            </div>

            <!-- User Message -->
            <div v-else class="flex items-start justify-end mb-4">
              <div class="bg-indigo-600 rounded-lg p-4 shadow-sm max-w-3xl">
                <p class="text-white">{{ message.text }}</p>
              </div>
              <div class="bg-indigo-100 p-2 rounded-full ml-3 flex-shrink-0">
                <span class="text-indigo-600 text-sm font-medium">You</span>
              </div>
            </div>

            <!-- Loading Message -->
            <div v-if="message.sender === 'loading'" class="flex items-start mb-4">
              <div class="bg-indigo-100 p-2 rounded-full mr-3 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 3l1.88 5.76h6.12l-4.94 3.59 1.88 5.76-4.94-3.59-4.94 3.59 1.88-5.76-4.94-3.59h6.12z"></path>
                </svg>
              </div>
              <div class="bg-white rounded-lg p-4 shadow-sm max-w-3xl">
                <div class="flex items-center space-x-2">
                  <div class="flex space-x-1">
                    <div class="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"></div>
                    <div class="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
                    <div class="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                  </div>
                  <span class="text-gray-500 text-sm">AI is thinking...</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Input -->
        <div class="border-t border-gray-200 p-4 flex items-center">
          <input
            type="text"
            placeholder="Type a follow-up question..."
            class="flex-grow px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            v-model="followUpQuery"
            @keyup.enter="handleFollowUpSubmit"
            :disabled="isLoading"
          />
          <button
            @click="handleFollowUpSubmit"
            :disabled="isLoading || !followUpQuery.trim()"
            class="ml-2 p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="!isLoading" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 2L11 13"></path>
              <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
            </svg>
            <div v-else class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </button>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 flex justify-between">
          <span>Based on your dashboard data • {{ new Date().toLocaleDateString() }}</span>
          <span class="text-indigo-600 cursor-pointer hover:underline" @click="exportConversation">Export conversation</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HTTP from '@/common/http'
import { mapGetters } from 'vuex'

export default {
  name: 'AISearch',
  props: [
    'id'
  ],
  computed: {
    ...mapGetters([
      'activeType'
    ])
  },
  data () {
    return {
      isSearchFocused: false,
      searchQuery: '',
      followUpQuery: '',
      showAIResponse: false,
      chatMessages: [],
      isLoading: false,
      sessionId: '',
      supervisor: '',
      costs: '',
      deployments: ''
    }
  },
  created: function () {
    this.sessionId = this.generateUUID()
  },
  watch: {
    chatMessages: {
      handler () {
        this.$nextTick(() => {
          if (this.$refs.messageContainer) {
            this.$refs.messageContainer.scrollTop = this.$refs.messageContainer.scrollHeight
          }
        })
      },
      deep: true
    }
  },
  methods: {
    handleBlur () {
      setTimeout(() => {
        if (!this.searchQuery) {
          this.isSearchFocused = false
        }
      }, 150)
    },

    async handleSearchSubmit () {
      if (!this.searchQuery.trim() || this.isLoading) return

      // Add user message
      this.chatMessages.push({
        sender: 'user',
        text: this.searchQuery
      })

      // Add loading indicator
      this.chatMessages.push({
        sender: 'loading',
        text: ''
      })

      this.showAIResponse = true
      this.isLoading = true

      const currentQuery = this.searchQuery
      this.searchQuery = ''

      try {
        await this.submitQuery(currentQuery)
      } catch (error) {
        // Remove loading indicator
        this.chatMessages = this.chatMessages.filter(msg => msg.sender !== 'loading')

        // Add error message
        this.chatMessages.push({
          sender: 'ai',
          text: 'Sorry, I encountered an error while processing your request. Please try again.'
        })

        console.error('Query submission error:', error)
      } finally {
        this.isLoading = false
      }
    },

    async handleFollowUpSubmit () {
      if (!this.followUpQuery.trim() || this.isLoading) return

      // Add user follow-up message
      this.chatMessages.push({
        sender: 'user',
        text: this.followUpQuery
      })

      // Add loading indicator
      this.chatMessages.push({
        sender: 'loading',
        text: ''
      })

      this.isLoading = true

      const currentQuery = this.followUpQuery
      this.followUpQuery = ''

      try {
        await this.submitQuery(currentQuery)
      } catch (error) {
        // Remove loading indicator
        this.chatMessages = this.chatMessages.filter(msg => msg.sender !== 'loading')

        // Add error message
        this.chatMessages.push({
          sender: 'ai',
          text: 'Sorry, I encountered an error while processing your request. Please try again.'
        })

        console.error('Follow-up submission error:', error)
      } finally {
        this.isLoading = false
      }
    },

    async submitQuery (query) {
      const payload = {
        query: query,
        supervisor: this.supervisor,
        costs: this.costs,
        deployments: this.deployments
      }

      try {
        const response = await HTTP.post(
          `insights/${this.activeType}/${this.id}/${this.sessionId}`,
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        )

        // Remove loading indicator
        this.chatMessages = this.chatMessages.filter(msg => msg.sender !== 'loading')

        // Add AI response
        this.chatMessages.push({
          sender: 'ai',
          text: response.data.response
        })
      } catch (error) {
        // Remove loading indicator
        this.chatMessages = this.chatMessages.filter(msg => msg.sender !== 'loading')

        // Show error message to user
        if (this.flashMessage) {
          this.flashMessage.error({
            message: `Failed to get AI response: ${error.message}`
          })
        }

        throw error
      }
    },

    startNewChat () {
      this.chatMessages = []
      this.sessionId = this.generateUUID()
      this.searchQuery = ''
      this.followUpQuery = ''
    },

    exportConversation () {
      const conversationText = this.chatMessages
        .map(msg => `${msg.sender === 'user' ? 'You' : 'AI'}: ${msg.text}`)
        .join('\n\n')

      const blob = new Blob([conversationText], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ai-conversation-${new Date().toISOString().split('T')[0]}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    generateUUID () {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/AISearch/SearchBar.vue